
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  FaArrowRight,
  FaBriefcase,
  FaLandmark,
  FaUser,
} from "react-icons/fa";

import Header from "./Header";
import Aktuellt from "./Aktuellt";

const kontakter = [
  ["Sofia Groth", "070-203 41 08", "sofia.groth@hudiksvall.se"],
];

/* =========================================================
   SECTION
========================================================= */

function Section({ children, className = "", id }) {
  return (
    <section
      id={id}
      className={`
        px-4
        py-16
        sm:px-6
        sm:py-20
        lg:px-10
        lg:py-28
        ${className}
      `}
    >
      <div className="mx-auto max-w-6xl">
        {children}
      </div>
    </section>
  );
}

/* =========================================================
   HEADING
========================================================= */

function Heading({ children, light = false }) {
  return (
    <h2
      className={`
        text-[30px]
        font-semibold
        leading-tight
        tracking-[-0.035em]
        sm:text-3xl
        lg:text-4xl
        ${light ? "text-white" : "text-neutral-900"}
      `}
    >
      {children}
    </h2>
  );
}

/* =========================================================
   CARD
========================================================= */

function Card({ number, title, text }) {
  return (
    <div
      className="
        group
        rounded-[24px]
        border
        border-neutral-200
        bg-white
        p-6
        transition-all
        duration-500
        sm:p-8
        hover:-translate-y-1
        hover:border-[#3BAFA3]/40
        hover:shadow-[0_24px_60px_rgba(15,23,42,0.08)]
      "
    >
      {number && (
        <div
          className="
            flex
            h-12
            w-12
            items-center
            justify-center
            rounded-xl
            bg-[#E8F7F5]
            text-sm
            font-semibold
            text-[#3BAFA3]
          "
        >
          {number}
        </div>
      )}

      <h3
        className="
          mb-3
          mt-6
          text-xl
          font-semibold
          tracking-[-0.025em]
          text-neutral-900
        "
      >
        {title}
      </h3>

      <div className="leading-relaxed text-neutral-600">
        {text}
      </div>
    </div>
  );
}

/* =========================================================
   AUDIENCE CARD
========================================================= */

function AudienceCard({
  icon: Icon,
  eyebrow,
  title,
  description,
  href,
}) {
  return (
    <Link
      to={href}
      className="
        group
        relative
        flex
        min-h-[300px]
        flex-col
        justify-between
        overflow-hidden
        rounded-[24px]
        border
        border-neutral-200
        bg-white
        p-6
        text-left
        text-neutral-900
        transition-all
        duration-500

        sm:min-h-[330px]
        sm:rounded-[26px]
        sm:p-8

        lg:min-h-[360px]
        lg:rounded-[28px]
        lg:p-9

        hover:-translate-y-1.5
        hover:border-[#3BAFA3]/40
        hover:shadow-[0_30px_70px_rgba(15,23,42,0.10)]
      "
    >
      {/* =================================================
          DEKORATIV CIRKEL
      ================================================= */}

      <div
        className="
          pointer-events-none
          absolute
          -right-24
          -top-24
          h-64
          w-64
          rounded-full
          bg-[#3BAFA3]/10
          transition-all
          duration-700

          group-hover:scale-125
          group-hover:bg-[#3BAFA3]/15
        "
      />

      {/* Extra mjuk cirkel i bakgrunden */}

      <div
        className="
          pointer-events-none
          absolute
          -right-10
          -top-10
          h-32
          w-32
          rounded-full
          bg-[#3BAFA3]/5
          blur-2xl
          transition-all
          duration-700

          group-hover:scale-125
          group-hover:bg-[#3BAFA3]/10
        "
      />

      <div className="relative">

        {/* =================================================
            IKON
        ================================================= */}

        <div
          className="
            flex
            h-12
            w-12
            items-center
            justify-center
            rounded-2xl
            bg-[#E8F7F5]
            text-[#3BAFA3]
            transition-all
            duration-500

            sm:h-14
            sm:w-14

            group-hover:scale-105
            group-hover:bg-[#3BAFA3]
            group-hover:text-white
          "
        >
          <Icon
            size={20}
            className="
              transition-transform
              duration-500
              group-hover:scale-110
            "
          />
        </div>

        {/* =================================================
            EYEBROW
        ================================================= */}

        <div
          className="
            mt-7
            text-[11px]
            font-semibold
            uppercase
            tracking-[0.16em]
            text-[#3BAFA3]

            sm:mt-9
            sm:text-xs
          "
        >
          {eyebrow}
        </div>

        {/* =================================================
            TITEL
        ================================================= */}

        <h3
          className="
            mt-3
            max-w-sm
            text-[24px]
            font-semibold
            leading-[1.12]
            tracking-[-0.035em]
            text-neutral-900

            sm:text-[27px]
          "
        >
          {title}
        </h3>

        {/* =================================================
            BESKRIVNING
        ================================================= */}

        <p
          className="
            mt-4
            max-w-sm
            text-[15px]
            leading-7
            text-neutral-500

            sm:mt-5
          "
        >
          {description}
        </p>
      </div>

      {/* =================================================
          LÄS MER
      ================================================= */}

      <div
        className="
          relative
          mt-8
          flex
          items-center
          gap-2.5
          text-sm
          font-semibold
          text-[#3BAFA3]

          sm:mt-10
        "
      >
        <span>Läs mer</span>

        <FaArrowRight
          size={11}
          className="
            transition-transform
            duration-500
            group-hover:translate-x-1.5
          "
        />
      </div>
    </Link>
  );
}

/* =========================================================
   HOME PAGE
========================================================= */

export default function HomePage() {
  const [showVideo, setShowVideo] = useState(false);

  /* -------------------------------------------------------
     Video modal / Escape
  ------------------------------------------------------- */

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setShowVideo(false);
      }
    };

    document.addEventListener("keydown", handleEscape);

    if (showVideo) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [showVideo]);

  return (
    <div className="min-h-screen overflow-x-hidden bg-white text-neutral-900">

      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="relative min-h-screen overflow-hidden bg-white">

        {/* Bakgrundsbild */}

        <img
          src="/hero.png"
          alt=""
          className="
            absolute
            inset-0
            h-full
            w-full
            object-cover
            object-center
          "
        />

        {/* Mjuk ton */}

        <div className="absolute inset-0 bg-white/15" />

        {/* Ljus fade från vänster */}

        <div
          className="
            absolute
            inset-y-0
            left-0
            w-full
            bg-gradient-to-r
            from-white/90
            via-white/60
            to-white/10
          "
        />

        {/* Mjuk fade längst ned */}

        <div
          className="
            absolute
            inset-x-0
            bottom-0
            h-64
            bg-gradient-to-t
            from-white/75
            via-white/30
            to-transparent
          "
        />

        {/* Header */}

        <Header />



{/* =================================================
    HERO CONTENT
================================================= */}

<div
  className="
    relative
    z-10
    mx-auto
    flex
    min-h-screen
    w-full
    max-w-[1400px]
    items-center
    px-4
    pb-12
    pt-20

    sm:px-6
    sm:pb-16
    sm:pt-28

    lg:px-10
    lg:pt-32

    xl:px-16
  "
>
<div
  className="
    mx-auto
    grid
    w-full
    max-w-[1050px]
    items-center

    sm:grid-cols-[minmax(0,680px)_300px]

    xl:max-w-none
    xl:grid-cols-[1fr_0.8fr]
  "
>
  {/* MOBIL / TABLET – LOGGA */}

{/* LOGGA */}
<div
  className="
    mb-6
    flex
    w-full
    items-center
   justify-center

    sm:col-start-2
    sm:row-start-1
    sm:mb-0

    xl:hidden

  "
>
<div
  className="
    relative
    h-[260px]
    w-[260px]
    shrink-0

    sm:h-[280px]
    sm:w-[280px]

    lg:h-[330px]
    lg:w-[330px]

    xl:h-[380px]
    xl:w-[380px]

    2xl:h-[430px]
    2xl:w-[430px]
  "
>
    <img
      src="/icon.png"
      alt="Räkna med mig"
      className="
        block
        h-full
        w-full
        object-contain
        drop-shadow-[0_18px_35px_rgba(15,23,42,0.12)]
      "
    />
  </div>
</div>

    {/* =================================================
        VÄNSTER – TEXT
    ================================================= */}

<div
  className="
    max-w-[760px]

    sm:col-start-1
    sm:row-start-1

    xl:col-start-1
  "
>
      {/* EYEBROW */}

      <div
        className="
          mb-5
          flex
          items-center
          gap-3

          sm:mb-8
          sm:gap-4
        "
      >
        <span className="h-px w-8 bg-[#3BAFA3] sm:w-12" />

        <span
          className="
            text-[10px]
            font-semibold
            uppercase
            tracking-[0.20em]
            text-[#3BAFA3]

            sm:text-[11px]
            sm:tracking-[0.24em]
          "
        >
          Räkna med mig
        </span>
      </div>


      {/* HUVUDRUBRIK */}

      <h1
        className="
          text-[42px]
          font-semibold
          leading-[0.98]
          tracking-[-0.06em]
          text-black

          sm:text-[52px]

          lg:text-[64px]

          xl:text-[80px]
        "
      >
        Vi bygger broar
      </h1>


      {/* GRÖN UNDERRUBRIK */}

      <div
        className="
          mt-4
          max-w-[700px]
          text-[21px]
          font-medium
          leading-[1.15]
          tracking-[-0.035em]
          text-[#3BAFA3]

          sm:mt-6
          sm:text-[27px]

          lg:text-[34px]

          xl:text-[40px]
        "
      >
        mellan människor{" "}
        <span className="text-neutral-900">
          och
        </span>{" "}
        möjligheter
      </div>


      {/* INTRO */}

      <p
        className="
          mt-5
          max-w-[570px]
          text-[16px]
          leading-7
          text-neutral-700

          sm:mt-8
          sm:text-[17px]

          lg:text-lg
        "
      >
        Tillsammans skapar vi vägar till arbete,
        studier och ökad delaktighet.
      </p>


      {/* =================================================
          PLAYKNAPP
          UNDER TEXTEN + CENTRERAD PÅ MOBIL
      ================================================= */}

      <div
        className="
        mt-10
          flex
          w-full
          justify-center

          sm:mt-8
          sm:justify-start

          xl:hidden
        "
      >
        <button
          type="button"
          onClick={() => setShowVideo(true)}
          aria-label="Se vårt syfte"
          className="
            group
            relative
            flex
            items-center
            gap-3
            rounded-full
            border
            border-black/15
            bg-white/60
            px-4
            py-2.5
            shadow-[0_10px_30px_rgba(15,23,42,0.08)]
            backdrop-blur-sm
            transition-all
            duration-300

            hover:-translate-y-1
            hover:border-[#3BAFA3]/60
            hover:bg-white/85
            hover:shadow-[0_16px_40px_rgba(59,175,163,0.16)]

            focus:outline-none
            focus:ring-2
            focus:ring-[#3BAFA3]/50
            focus:ring-offset-2
          "
        >
          <span
            className="
              flex
              h-9
              w-9
              shrink-0
              items-center
              justify-center
              rounded-full
              bg-[#3BAFA3]
              transition-all
              duration-300

              group-hover:scale-105
              group-hover:bg-[#31988e]
            "
          >
            <span
              className="
                ml-1
                h-0
                w-0
                border-y-[5px]
                border-l-[8px]
                border-y-transparent
                border-l-white
              "
            />
          </span>

          <span
            className="
              text-[12px]
              font-semibold
              tracking-[-0.01em]
              text-neutral-900
            "
          >
            Syfte - Räkna med mig!
          </span>
        </button>
      </div>


      {/* =================================================
          NAVIGATION
          DOLD PÅ SMÅ SKÄRMAR
      ================================================= */}

   <div
  className="
    hidden
    sm:block
    mt-8

    w-full
    max-w-[clamp(420px,42vw,650px)]

    sm:mt-8
    lg:mt-10
    xl:mt-12
  "
>
        <div className="border-y border-black/10">

          {/* 01 */}

          <Link
            to="/arbetsgivare"
            className="
              group
              flex
              items-center
              justify-between
              gap-4
              py-5
              transition-all
              duration-300

              sm:gap-6

              hover:pl-3
            "
          >
            <div className="flex min-w-0 items-center gap-3 sm:gap-5">

              <span
                className="
                  shrink-0
                  text-[11px]
                  font-semibold
                  tracking-[0.15em]
                  text-neutral-400
                "
              >
                01
              </span>

              <div className="min-w-0">

                <div
                  className="
                    text-[10px]
                    font-semibold
                    uppercase
                    tracking-[0.15em]
                    text-[#3BAFA3]

                    sm:tracking-[0.18em]
                  "
                >
                  För organisationer
                </div>

                <div
                  className="
                    mt-1
                    text-[15px]
                    font-semibold
                    tracking-[-0.01em]
                    text-neutral-900

                    sm:text-base

                    lg:text-lg
                  "
                >
                  Företag eller föreningar
                </div>

              </div>
            </div>

            <FaArrowRight
              size={14}
              className="
                shrink-0
                text-[#3BAFA3]
                transition-transform
                duration-300
                group-hover:translate-x-2
              "
            />
          </Link>


          {/* 02 */}

          <Link
            to="/deltagare"
            className="
              group
              flex
              items-center
              justify-between
              gap-4
              border-t
              border-black/10
              py-5
              transition-all
              duration-300

              sm:gap-6

              hover:pl-3
            "
          >
            <div className="flex min-w-0 items-center gap-3 sm:gap-5">

              <span
                className="
                  shrink-0
                  text-[11px]
                  font-semibold
                  tracking-[0.15em]
                  text-neutral-400
                "
              >
                02
              </span>

              <div className="min-w-0">

                <div
                  className="
                    text-[10px]
                    font-semibold
                    uppercase
                    tracking-[0.15em]
                    text-[#3BAFA3]

                    sm:tracking-[0.18em]
                  "
                >
                  För dig
                </div>

                <div
                  className="
                    mt-1
                    text-[15px]
                    font-semibold
                    tracking-[-0.01em]
                    text-neutral-900

                    sm:text-base

                    lg:text-lg
                  "
                >
                  Som är ung eller närstående
                </div>

              </div>
            </div>

            <FaArrowRight
              size={14}
              className="
                shrink-0
                text-[#3BAFA3]
                transition-transform
                duration-300
                group-hover:translate-x-2
              "
            />
          </Link>


          {/* 03 */}

          <Link
            to="/myndigheter"
            className="
              group
              flex
              items-center
              justify-between
              gap-4
              border-t
              border-black/10
              py-5
              transition-all
              duration-300

              sm:gap-6

              hover:pl-3
            "
          >
            <div className="flex min-w-0 items-center gap-3 sm:gap-5">

              <span
                className="
                  shrink-0
                  text-[11px]
                  font-semibold
                  tracking-[0.15em]
                  text-neutral-400
                "
              >
                03
              </span>

              <div className="min-w-0">

                <div
                  className="
                    text-[10px]
                    font-semibold
                    uppercase
                    tracking-[0.15em]
                    text-[#3BAFA3]

                    sm:tracking-[0.18em]
                  "
                >
                  Om projektet
                </div>

                <div
                  className="
                    mt-1
                    text-[15px]
                    font-semibold
                    tracking-[-0.01em]
                    text-neutral-900

                    sm:text-base

                    lg:text-lg
                  "
                >
                  Om Räkna med mig
                </div>

              </div>
            </div>

            <FaArrowRight
              size={14}
              className="
                shrink-0
                text-[#3BAFA3]
                transition-transform
                duration-300
                group-hover:translate-x-2
              "
            />
          </Link>

        </div>
      </div>
    </div>


    {/* =================================================
        DESKTOP – LOGGA + PLAY
        VISAS ENDAST PÅ XL
    ================================================= */}

    <div
      className="
        relative
        hidden
        w-full
        items-center
        justify-center

        xl:flex
      "
    >
      <div
        className="
          relative
          flex
          w-full
          flex-col
          items-center
          justify-center
        "
      >

        {/* LOGGA */}

        <div
          className="
            group
            relative
            h-[380px]
            w-[380px]
            shrink-0

            2xl:h-[430px]
            2xl:w-[430px]
          "
        >
          <img
            src="/icon.png"
            alt="Räkna med mig"
            className="
              block
              h-full
              w-full
              object-contain

              drop-shadow-[0_18px_35px_rgba(15,23,42,0.12)]

              transition-all
              duration-700
              ease-out

              group-hover:-translate-y-1
              group-hover:drop-shadow-[0_24px_45px_rgba(15,23,42,0.16)]
            "
          />
        </div>


        {/* PLAYKNAPP – DESKTOP */}

        <button
          type="button"
          onClick={() => setShowVideo(true)}
          aria-label="Se vårt syfte"
          className="
            group
            relative
            mt-5
            flex
            items-center
            gap-3
            rounded-full
            border
            border-black/15
            bg-white/60
            px-5
            py-3
            shadow-[0_10px_30px_rgba(15,23,42,0.08)]
            backdrop-blur-sm
            transition-all
            duration-300

            hover:-translate-y-1
            hover:border-[#3BAFA3]/60
            hover:bg-white/85
            hover:shadow-[0_16px_40px_rgba(59,175,163,0.16)]

            focus:outline-none
            focus:ring-2
            focus:ring-[#3BAFA3]/50
            focus:ring-offset-2
          "
        >
          <span
            className="
              flex
              h-10
              w-10
              shrink-0
              items-center
              justify-center
              rounded-full
              bg-[#3BAFA3]
              transition-all
              duration-300

              group-hover:scale-105
              group-hover:bg-[#31988e]
            "
          >
            <span
              className="
                ml-1
                h-0
                w-0
                border-y-[6px]
                border-l-[9px]
                border-y-transparent
                border-l-white
              "
            />
          </span>

          <span
            className="
              text-[13px]
              font-semibold
              tracking-[-0.01em]
              text-neutral-900
            "
          >
            Syfte - Räkna med mig!
          </span>
        </button>

      </div>
    </div>

  </div>
</div>

 </section>

{/* =========================================================
    AKTUELLT
========================================================= */}

<Aktuellt />


     {/* =========================================================
    OM
========================================================= */}

<Section id="om" className="bg-black text-white">

  <div
    className="
      grid
      items-center
      gap-10
      sm:gap-12
      lg:gap-16
      lg:grid-cols-[0.8fr_1.2fr]
    "
  >

    {/* Vänster */}

    <div>

      <div className="mb-5 flex items-center gap-3">
        <span className="h-px w-8 bg-[#3BAFA3]" />

        <span
          className="
            text-[11px]
            font-semibold
            uppercase
            tracking-[0.16em]
            text-[#3BAFA3]
            sm:text-xs
            sm:tracking-[0.18em]
          "
        >
          Om Räkna med mig
        </span>
      </div>

   <h2
  className="
    text-3xl
    font-semibold
    leading-tight
    tracking-[-0.04em]
    text-white
    sm:text-4xl
    md:text-5xl
  "
>
  Tillsammans skapar vi vägar
</h2>

    </div>

    {/* Höger */}

    <div
      className="
        space-y-5
        text-base
        leading-7
        text-white/70
        sm:space-y-6
        sm:text-lg
        sm:leading-8
      "
    >
      <p>
        Räkna med mig är ett projekt där vi tillsammans
        skapar vägar till arbete, studier och delaktighet.
      </p>

      <p>
        Vi samlar kommun, myndigheter, företag och andra
        aktörer kring ett gemensamt mål: att göra det
        lättare för unga med aktivitetsersättning att hitta
        rätt stöd och rätt möjligheter utifrån sin egen
        situation.
      </p>

      <p>
        Målet är att fler ska få rätt stöd, vid rätt tid,
        och kunna ta nästa steg mot arbete, studier eller
        en annan väg framåt.
      </p>
    </div>

  </div>
</Section>

      {/* =========================================================
          MÅLGRUPPER
      ========================================================= */}

      <Section className="bg-[#F8FAFA]">

        <div className="mb-9 max-w-2xl sm:mb-12">

          <div className="mb-5 flex items-center gap-3">
            <span className="h-px w-8 bg-[#3BAFA3]" />

            <span
              className="
                text-[11px]
                font-semibold
                uppercase
                tracking-[0.16em]
                text-[#3BAFA3]

                sm:text-xs
                sm:tracking-[0.18em]
              "
            >
              Hitta rätt väg
            </span>
          </div>

          <Heading>
            Vad söker du?
          </Heading>

          <p
            className="
              mt-4
              text-base
              leading-7
              text-neutral-600

              sm:mt-5
              sm:text-lg
              sm:leading-8
            "
          >
            Här kan du läsa mer utifrån vem du är och hitta
            den information som är relevant för dig.
          </p>

        </div>

        {/* Kort */}

        <div className="grid gap-4 sm:gap-5 lg:grid-cols-3">

          <AudienceCard
            icon={FaUser}
            eyebrow="För dig"
            title="Som är ung eller närstående"
            description="Information, stöd och vägar vidare för dig som är ung eller står nära någon som behöver stöd."
            href="/deltagare"
          />

          <AudienceCard
            icon={FaBriefcase}
            eyebrow="För organisationer"
            title="Företag eller föreningar"
            description="Så kan du som arbetsgivare, företag eller förening vara med och skapa fler möjligheter."
            href="/arbetsgivare"
          />

          <AudienceCard
            icon={FaLandmark}
            eyebrow="Om projektet"
            title="Om Räkna med mig"
            description="Läs mer om projektets bakgrund, arbetssätt, mål och om de aktörer som samverkar."
            href="/myndigheter"
          />

        </div>
      </Section>
{/* =========================================================
    PROJEKTFAKTA
========================================================= */}

<Section className="bg-white">

  <div
    className="
      grid
      gap-10

      sm:gap-14

      lg:grid-cols-[0.7fr_1.3fr]
      lg:items-start
    "
  >

    {/* Vänster */}

    <div>

      <div className="mb-5 flex items-center gap-3">
        <span className="h-px w-8 bg-[#3BAFA3]" />

        <span
          className="
            text-[11px]
            font-semibold
            uppercase
            tracking-[0.16em]
            text-[#3BAFA3]

            sm:text-xs
            sm:tracking-[0.18em]
          "
        >
          Om projektet
        </span>
      </div>

      <Heading>
        Projektfakta
      </Heading>

      <p
        className="
          mt-4
          max-w-md
          text-base
          leading-7
          text-slate-600

          sm:mt-5
        "
      >
        Räkna med mig drivs genom samverkan mellan kommuner
        och samhällsaktörer med fokus på unga och vägen
        vidare mot arbete, studier och delaktighet.
      </p>

    </div>

    {/* Höger */}

    <div className="divide-y divide-neutral-200">

      {/* Projektägare */}

      <div
        className="
          grid
          gap-2
          py-5

          sm:grid-cols-[180px_1fr]
          sm:gap-8
          sm:py-7
        "
      >
        <div
          className="
            text-[10px]
            font-semibold
            uppercase
            tracking-[0.16em]
            text-neutral-400

            sm:text-xs
          "
        >
          Projektägare
        </div>

        <div>
          <div className="font-semibold text-neutral-900">
            Jobbcentrum
          </div>

          <div className="mt-1 text-sm text-slate-500">
            Hudiksvalls kommun
          </div>
        </div>
      </div>

      {/* Kommuner */}

      <div
        className="
          grid
          gap-2
          py-5

          sm:grid-cols-[180px_1fr]
          sm:gap-8
          sm:py-7
        "
      >
        <div
          className="
            text-[10px]
            font-semibold
            uppercase
            tracking-[0.16em]
            text-neutral-400

            sm:text-xs
          "
        >
          Kommuner
        </div>

        <div
          className="
            font-semibold
            leading-6
            text-neutral-900
          "
        >
          Hudiksvall · Ljusdal · Nordanstig
        </div>
      </div>

      {/* Finansiär */}

      <div
        className="
          grid
          gap-2
          py-5

          sm:grid-cols-[180px_1fr]
          sm:gap-8
          sm:py-7
        "
      >
        <div
          className="
            text-[10px]
            font-semibold
            uppercase
            tracking-[0.16em]
            text-neutral-400

            sm:text-xs
          "
        >
          Finansiär
        </div>

        <div className="font-semibold text-neutral-900">
          Samordningsförbundet Gävleborg
        </div>
      </div>

      {/* Projektperiod */}

      <div
        className="
          grid
          gap-2
          py-5

          sm:grid-cols-[180px_1fr]
          sm:gap-8
          sm:py-7
        "
      >
        <div
          className="
            text-[10px]
            font-semibold
            uppercase
            tracking-[0.16em]
            text-neutral-400

            sm:text-xs
          "
        >
          Projektperiod
        </div>

        <div
          className="
            text-xl
            font-semibold
            tracking-tight
            text-[#3BAFA3]
          "
        >
          2024–2028
        </div>
      </div>

      {/* Samverkande aktörer */}

      <div
        className="
          grid
          gap-3
          py-5

          sm:grid-cols-[180px_1fr]
          sm:gap-8
          sm:py-7
        "
      >
        <div
          className="
            text-[10px]
            font-semibold
            uppercase
            tracking-[0.16em]
            text-neutral-400

            sm:text-xs
          "
        >
          Samverkande aktörer
        </div>

        <div
          className="
            flex
            flex-wrap
            gap-x-5
            gap-y-2
            text-sm
            font-medium
            leading-6
            text-neutral-700
          "
        >
          <span>Arbetsförmedlingen</span>
          <span>Försäkringskassan</span>
          <span>Region Gävleborg</span>
        </div>
      </div>

    </div>
  </div>
</Section>

   
{/* =========================================================
    KONTAKT
========================================================= */}

<Section
  id="kontakt"
  className="bg-black text-white"
>
  <div
    className="
      grid
      gap-10

      xl:grid-cols-[0.8fr_1.2fr]
      xl:items-center
      xl:gap-20
    "
  >

    {/* =====================================================
        TEXT
    ===================================================== */}

    <div className="min-w-0">

      <div className="mb-5 flex items-center gap-3">
        <span className="h-px w-8 bg-[#6ED1C5]" />

        <span
          className="
            text-[10px]
            font-semibold
            uppercase
            tracking-[0.16em]
            text-[#6ED1C5]

            sm:text-xs
            sm:tracking-[0.18em]
          "
        >
          Hör av dig
        </span>
      </div>

      <Heading light>
        Kontakt
      </Heading>

      <p
        className="
          mt-4
          max-w-xl
          text-sm
          leading-6
          text-white/60

          sm:mt-6
          sm:text-lg
          sm:leading-8
        "
      >
        Vill du veta mer om projektet, våra arbetssätt
        eller möjligheterna till samverkan? Kontakta oss
        gärna så berättar vi mer.
      </p>

    </div>

    {/* =====================================================
        KONTAKTPERSON
    ===================================================== */}

    <div
      className="
        group
        min-w-0
        overflow-hidden
        rounded-[18px]
        bg-white
        shadow-[0_14px_40px_rgba(0,0,0,0.055)]
        ring-1
        ring-black/[0.04]

        sm:rounded-[22px]
      "
    >
      <div
        className="
          grid
          grid-cols-[0.9fr_1.1fr]
        "
      >

        {/* =================================================
            BILD
        ================================================= */}

        <div
          className="
            relative
            h-[115px]
            overflow-hidden
            bg-[#EAF5F3]

            sm:h-[170px]

            md:h-[200px]
          "
        >
          <img
            src="/Sofia.jpg"
            alt="Sofia Groth"
            className="
              h-full
              w-full
              object-cover
              object-center

              transition-transform
              duration-700

              group-hover:scale-[1.02]
            "
          />

          {/* Mjuk fade längst ner */}

          <div
            className="
              pointer-events-none
              absolute
              inset-x-0
              bottom-0
              h-12
              bg-gradient-to-t
              from-black/20
              to-transparent
            "
          />

        
        </div>

        {/* =================================================
            KONTAKTINFORMATION
        ================================================= */}

        <div
          className="
            flex
            min-w-0
            flex-col
            justify-center
            p-3

            sm:p-5

            md:p-6
          "
        >
          <p
            className="
              text-[8px]
              font-semibold
              uppercase
              tracking-[0.16em]
              text-[#3BAFA3]

              sm:text-[11px]
            "
          >
            Kontaktperson
          </p>

          <h3
            className="
              mt-1
              text-base
              font-semibold
              tracking-[-0.03em]
              text-neutral-900

              sm:mt-2
              sm:text-2xl
            "
          >
            Sofia Groth
          </h3>

          <p
            className="
              mt-0.5
              text-[10px]
              text-neutral-500

              sm:text-sm
            "
          >
            Insatsledare
          </p>

          <div
            className="
              my-2.5
              h-px
              bg-neutral-100

              sm:my-5
            "
          />

          <div
            className="
              space-y-1
              text-[9px]
              leading-4
              text-neutral-600

              sm:space-y-1.5
              sm:text-sm
              sm:leading-6
            "
          >
            {kontakter.map(([, phone, email]) => (
              <div key={email}>

                <a
                  href={`mailto:${email}`}
                  className="
                    block
                    break-words
                    transition-colors
                    hover:text-[#3BAFA3]
                  "
                >
                  {email}
                </a>

                <a
                  href={`tel:${phone.replace(/\s/g, "")}`}
                  className="
                    block
                    transition-colors
                    hover:text-[#3BAFA3]
                  "
                >
                  {phone}
                </a>

              </div>
            ))}
          </div>
        </div>

      </div>
    </div>

  </div>
</Section>



      {/* =========================================================
          FOOTER
      ========================================================= */}

      <footer
        className="
          border-t
          border-white/10
          bg-neutral-950
          text-white
        "
      >
        <div
          className="
            mx-auto
            flex
            max-w-6xl
            flex-col
            gap-3
            px-4
            py-6
            text-center
            text-xs
            text-white/35

            sm:px-6
            sm:py-8
            sm:text-sm

            lg:flex-row
            lg:items-center
            lg:justify-between
            lg:px-10

            xl:px-16
          "
        >
          <p>
            © Räkna med mig
          </p>

          <p>
            Hudiksvall · Ljusdal · Nordanstig
          </p>
        </div>
      </footer>

      {/* =========================================================
          VIDEO MODAL
      ========================================================= */}

      {showVideo && (
        <div
          className="
            fixed
            inset-0
            z-[100]
            flex
            items-center
            justify-center
            bg-black/90
            p-3
            backdrop-blur-md

            sm:p-4

            md:p-8
          "
          onClick={() => setShowVideo(false)}
        >
          <div
            className="
              relative
              w-full
              max-w-5xl
              overflow-hidden
              rounded-xl
              bg-black
              shadow-[0_30px_100px_rgba(0,0,0,0.60)]

              sm:rounded-2xl
            "
            onClick={(event) => event.stopPropagation()}
          >

            {/* Stäng */}

            <button
              type="button"
              onClick={() => setShowVideo(false)}
              aria-label="Stäng film"
              className="
                absolute
                right-2
                top-2
                z-20
                flex
                h-10
                w-10
                items-center
                justify-center
                rounded-full
                bg-black/60
                text-2xl
                leading-none
                text-white
                backdrop-blur-sm
                transition-all
                duration-200

                sm:right-4
                sm:top-4
                sm:h-11
                sm:w-11

                hover:scale-105
                hover:bg-black/80
              "
            >
              ×
            </button>

            {/* Video */}

            <video
              className="
                aspect-video
                h-auto
                w-full
                bg-black
                object-contain
              "
              controls
              autoPlay
              playsInline
            >
              <source
                src="/projektfilm.mp4"
                type="video/mp4"
              />

              Din webbläsare stödjer inte video.
            </video>

          </div>
        </div>
      )}

    </div>
  );
}

